import React, { FunctionComponent, useState } from "react";
import { Box } from "@mui/material";
import { formatDateTime } from "src/utils/date/dateHelper";
import { McGeneralDiseaseItem, mcGeneralDiseaseService } from "src/api/medcards/mcGeneralDiseaseService";
import { useTranslate } from "src/locales";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";

const dateIcon = "/assets/icons/clarity_date-line.svg";
const noteIcon = "/assets/icons/material-symbols_note-outline.svg";
const editIcon = "/assets/icons/mdi_pencil-outline.svg";
const trashIcon = "/assets/icons/mynaui_trash.svg";

interface SpecialNoteCardProps {
  note: string;
  operationDate: string;
  operator: string;
  noteText: string;
  id: number;
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const InfoRow: FunctionComponent<{ icon: string; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box
    sx={{
      alignSelf: "stretch",
      borderTop: "1px solid rgba(199, 199, 199, 0.3)",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "6px 16px",
    }}
  >
    <Box
      sx={{
        alignSelf: "stretch",
        height: "32px",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "8px 0px",
        boxSizing: "border-box",
        gap: "8px",
      }}
    >
      <Box component="img" src={icon} sx={{ height: "16px", width: "16px", position: "relative" }} alt="" />
      <Box sx={{ width: "110px", position: "relative", fontWeight: 600, display: "inline-block", flexShrink: 0 }}>
        {label}
      </Box>
      <Box sx={{ position: "relative", fontWeight: 300 }}>{value}</Box>
    </Box>
  </Box>
);

const SpecialNoteCard: FunctionComponent<SpecialNoteCardProps> = ({
  note,
  operationDate,
  operator,
  noteText,
  id,
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await mcGeneralDiseaseService.deleteGeneralDisease(id);
      onDelete?.(id);
      onDeleteSuccess?.();
      return true;
    } catch (error) {
      console.error("Error deleting special note:", error);
      onDeleteError?.();
      return false;
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "318px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "left",
          fontSize: "12.01px",
          color: "#101010",
          fontFamily: 'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <Box
          sx={{
            width: "318px",
            boxShadow: "0px 4.0019307136535645px 7px -5px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px rgba(199, 199, 199, 0.25)",
            borderRadius: "8px",
            backgroundColor: "#fff",
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              backgroundColor: "#f9f9f9",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: "8px 16px",
              fontSize: "16.01px",
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                minHeight: "48px",
                height: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    position: "relative",
                    fontWeight: 600,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.4,
                    maxHeight: "2.8em",
                  }}
                >
                  {note}
                </Box>
                <Box
                  component="img"
                  src={trashIcon}
                  alt={t("special-note.delete")}
                  onClick={handleDeleteClick}
                  sx={{ width: "24px", position: "relative", maxHeight: "100%", cursor: "pointer" }}
                />
                <Box
                  component="img"
                  src={editIcon}
                  alt={t("special-note.edit")}
                  onClick={() =>
                    onEdit?.({
                      id,
                      name: note,
                      pdate: operationDate,
                      ctime: operationDate,
                      cuser: operator,
                      privatenotes: noteText,
                      note: noteText,
                    } as McGeneralDiseaseItem)
                  }
                  sx={{ width: "24px", height: "24px", marginLeft: "8px", position: "relative", cursor: "pointer" }}
                />
              </Box>
            </Box>
          </Box>

          <InfoRow icon={dateIcon} label={t("special-note.operation-date")} value={operationDate || ""} />
          <InfoRow icon={dateIcon} label={t("special-note.operation-user-short")} value={operator || ""} />
          <InfoRow icon={noteIcon} label={t("special-note.note-section")} value={noteText || ""} />
        </Box>
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleteConfirm}
        onSuccess={(success) => {
          if (success) {
            setDeleteDialogOpen(false);
          }
        }}
        title={t("special-note.delete-confirm-title")}
        content={t("special-note.delete-confirm-content")}
        successMessage={t("special-note.delete-success")}
        errorMessage={t("special-note.delete-error")}
        hideSnackbar={true}
      />
    </>
  );
};

interface SpecialNotesCardListProps {
  data?: McGeneralDiseaseItem[];
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const SpecialNotesCardList: FunctionComponent<SpecialNotesCardListProps> = ({
  data = [],
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslate();
  const items: SpecialNoteCardProps[] = data.map((item) => ({
    note: item.name || t("special-note.special-notes"),
    operationDate: formatDateTime(item.pdate || item.ctime, "dd.MM.yyyy HH:mm") || "",
    operator: item.cuser || "",
    noteText: item.privatenotes || item.note || "",
    id: item.id,
    onDelete,
    onEdit: () => onEdit?.(item),
    onDeleteSuccess,
    onDeleteError,
  }));

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "12px" }}>
      {items.map((item, index) => (
        <SpecialNoteCard key={`${item.note}-${item.operationDate}-${index}`} {...item} />
      ))}
    </Box>
  );
};

export default SpecialNotesCardList;

